import { createAsyncThunk } from '@reduxjs/toolkit'
import { projectsAPI } from '../../utils/supabase'

// Async Thunks for Projects
export const fetchProjects = createAsyncThunk(
  'tasks/fetchProjects',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await projectsAPI.getAllProjects(userId)
      if (error) throw error
      return data || []
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createProject = createAsyncThunk(
  'tasks/createProject',
  async ({ userId, projectData }, { rejectWithValue }) => {
    try {
      const { data, error } = await projectsAPI.createProject(userId, projectData)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateProject = createAsyncThunk(
  'tasks/updateProject',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const { data, error } = await projectsAPI.updateProject(id, updates)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteProject = createAsyncThunk(
  'tasks/deleteProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const { error } = await projectsAPI.deleteProject(projectId)
      if (error) throw error
      return projectId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateProjectOrder = createAsyncThunk(
  'tasks/updateProjectOrder',
  async ({ projects }, { rejectWithValue, dispatch }) => {
    try {
      // Immediately update the Redux state for optimistic UI updates
      dispatch({ type: 'tasks/updateProjectOrderOptimistic', payload: projects })

      // Use the projectsAPI method for database updates
      const { data, error } = await projectsAPI.updateProjectOrder(projects)
      if (error) throw error

      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Projects reducers
export const projectsReducers = {
  // Fetch Projects
  [fetchProjects.pending]: (state) => {
    state.loading.projects = true
    state.error.projects = null
  },
  [fetchProjects.fulfilled]: (state, action) => {
    state.loading.projects = false
    state.projects = action.payload
    state.initialized.projects = true
  },
  [fetchProjects.rejected]: (state, action) => {
    state.loading.projects = false
    state.error.projects = action.payload
  },

  // Create Project
  [createProject.pending]: (state) => {
    state.loading.projects = true
    state.error.projects = null
  },
  [createProject.fulfilled]: (state, action) => {
    state.loading.projects = false
    state.projects.unshift(action.payload)
  },
  [createProject.rejected]: (state, action) => {
    state.loading.projects = false
    state.error.projects = action.payload
  },

  // Update Project
  [updateProject.pending]: (state) => {
    state.loading.projects = true
    state.error.projects = null
  },
  [updateProject.fulfilled]: (state, action) => {
    state.loading.projects = false
    const index = state.projects.findIndex(project => project.id === action.payload.id)
    if (index !== -1) {
      state.projects[index] = action.payload
    }
  },
  [updateProject.rejected]: (state, action) => {
    state.loading.projects = false
    state.error.projects = action.payload
  },

  // Delete Project
  [deleteProject.pending]: (state) => {
    state.loading.projects = true
    state.error.projects = null
  },
  [deleteProject.fulfilled]: (state, action) => {
    state.loading.projects = false
    state.projects = state.projects.filter(project => project.id !== action.payload)
  },
  [deleteProject.rejected]: (state, action) => {
    state.loading.projects = false
    state.error.projects = action.payload
  },

  // Update Project Order
  [updateProjectOrder.fulfilled]: (state, action) => {
    // Update projects with new display_order values from API response
    // Note: We don't sort globally because each status group has independent display_order values
    // Selectors (like selectActiveProjects) filter by status first, then sort by display_order
    action.payload.forEach(updatedProject => {
      const index = state.projects.findIndex(project => project.id === updatedProject.id)
      if (index !== -1) {
        state.projects[index] = { ...state.projects[index], ...updatedProject }
      }
    })
  },
  [updateProjectOrder.rejected]: (state, action) => {
    state.error.projects = action.payload
  }
}

// Regular reducers for internal actions
export const projectsRegularReducers = {
  updateProjectOrderOptimistic: (state, action) => {
    // Optimistically update project order in state
    // Create a map of project IDs to new display_order values
    const displayOrderMap = new Map(
      action.payload.map((project, index) => [project.id, index])
    )

    // Update only the display_order for matching projects, preserve all others
    // Note: We don't sort globally because each status group has independent display_order values
    // Selectors (like selectActiveProjects) filter by status first, then sort by display_order
    state.projects = state.projects.map(project => {
      if (displayOrderMap.has(project.id)) {
        return { ...project, display_order: displayOrderMap.get(project.id) }
      }
      return project
    })
  },
  clearProjects: (state) => {
    state.projects = []
    state.loading.projects = false
    state.error.projects = null
    state.initialized.projects = false
  }
}
